import { ArrowLeft, Shuffle, Trash2 } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ConfirmationDrawer } from './ConfirmationDrawer';
import { useState } from 'react';

export function FullWeekView({
  weekData,
  daysOrder,
  weekLabel,
  onBack,
  onViewMealDetails,
  onSwap,
  onRemove,
  isLargeScreen = false,
}) {
  const days = Array.isArray(daysOrder) && daysOrder.length === 7
    ? daysOrder
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const mealTypes = ['Breakfast', 'Lunch', 'Dinner'];

  // Helper function to get meal for a specific day and type
  const getMealForDayAndType = (day, mealType) => {
    const dayMeals = weekData.days[day] || [];
    return dayMeals.find((meal) => meal.mealType === mealType) || null;
  };

  // Helper function to check if a meal name appears multiple times in the week
  const getMealCount = (mealName) => {
    let count = 0;
    days.forEach((day) => {
      const dayMeals = weekData.days[day] || [];
      dayMeals.forEach((meal) => {
        if (meal.name === mealName) {
          count++;
        }
      });
    });
    return count;
  };

  // State for confirmation drawer
  const [confirmationMealId, setConfirmationMealId] = useState(null);

  return (
    <div className="min-h-screen bg-[#F8F7F5]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div
          className="mx-auto py-4 flex items-center gap-4"
          style={{
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem',
            maxWidth: isLargeScreen ? '1400px' : '100%'
          }}
        >
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            aria-label="Back to meal plan"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>
          <div>
            <h1 className="text-xl">Full Week View</h1>
            <p className="text-sm text-[#757575]">{weekLabel || weekData.label}</p>
          </div>
        </div>
      </header>

      {/* Desktop Grid View */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-6">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-white p-4 text-left sticky left-0 z-10 border-r border-gray-200">
                  <span className="text-sm font-medium text-[#757575]">Meal</span>
                </th>
                {days.map((day) => (
                  <th key={day} className="bg-white p-4 text-center border-r border-gray-200 last:border-r-0">
                    <span className="font-medium text-gray-900">{day}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mealTypes.map((mealType) => (
                <tr key={mealType}>
                  <td className="bg-white p-4 sticky left-0 z-10 border-r border-b border-gray-200 align-top">
                    <span className="text-sm font-medium text-gray-900">{mealType}</span>
                  </td>
                  {days.map((day) => {
                    const meal = getMealForDayAndType(day, mealType);
                    const isRepeated = meal && getMealCount(meal.name) > 1;

                    return (
                      <td
                        key={`${day}-${mealType}`}
                        className="bg-white p-3 border-r border-b border-gray-200 last:border-r-0 align-top"
                      >
                        {meal ? (
                          <div className={`bg-[#F8F7F5] rounded-2xl p-3 hover:shadow-md transition-shadow relative`}>
                              {/* Duplicate Indicator Badge */}
                              {isRepeated && (
                                <div 
                                  className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 bg-accent rounded-full flex items-center justify-center shadow-md z-10"
                                  aria-label={`Repeated ${getMealCount(meal.name)} times`}
                                  title={`This meal appears ${getMealCount(meal.name)} times this week`}
                                >
                                  <span className="text-[#643C35] text-xs font-semibold">
                                    {getMealCount(meal.name)}x
                                  </span>
                                </div>
                              )}
                              
                              <button
                                onClick={() => onViewMealDetails(meal.id)}
                                className="w-full text-left"
                              >
                              <div className="aspect-square w-full rounded-xl overflow-hidden mb-2 bg-gray-100">
                                <ImageWithFallback
                                  src={meal.image}
                                  alt={meal.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <h4 className="text-sm mb-1 line-clamp-2">{meal.name}</h4>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {meal.tags.slice(0, 2).map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-0.5 bg-[#E8F0ED] text-[#6B8A78] rounded-full text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <p className="text-xs text-[#757575]">Serves {meal.serves}</p>
                            </button>
                            
                            {/* Quick Actions */}
                            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSwap(meal.id);
                                }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white hover:bg-gray-100 transition-colors"
                                aria-label={`Swap ${meal.name}`}
                              >
                                <Shuffle className="w-3.5 h-3.5 text-gray-600" />
                                <span className="text-xs text-gray-600">Swap</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmationMealId(meal.id);
                                }}
                                className="p-1.5 rounded-lg bg-[#F3F4F6] hover:bg-[#B85A5A] transition-colors group"
                                aria-label={`Remove ${meal.name}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-[#525252] group-hover:text-white transition-colors" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-2xl p-6 border-2 border-dashed border-gray-200 flex items-center justify-center min-h-[120px]">
                            <span className="text-xs text-[#757575]">No meal</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center shadow-sm">
              <span className="text-[#643C35] text-xs font-semibold">2x</span>
            </div>
            <span className="text-sm text-[#757575]">= Meal appears multiple times this week</span>
          </div>
        </div>
      </div>

      {/* Mobile List View */}
      <div className="md:hidden max-w-md mx-auto px-6 py-6">
        {days.map((day) => {
          const dayMeals = weekData.days[day] || [];
          const hasMeals = dayMeals.length > 0;

          return (
            <div key={day} className="mb-6">
              <h2 className="text-xl mb-3">{day}</h2>
              
              {hasMeals ? (
                <div className="space-y-3">
                  {mealTypes.map((mealType) => {
                    const meal = getMealForDayAndType(day, mealType);
                    const isRepeated = meal && getMealCount(meal.name) > 1;

                    return (
                      <div key={mealType}>
                        <p className="text-sm text-gray-500 mb-2 px-1">{mealType}</p>
                       {meal ? (
                          <div className={`bg-white rounded-3xl p-4 shadow-sm hover:shadow-md transition-shadow relative ${isRepeated ? 'ring-2 ring-accent ring-offset-2' : ''}`}>
                            {/* Duplicate Indicator Badge */}
                            {isRepeated && (
                              <div 
                                className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 bg-accent rounded-full flex items-center justify-center shadow-md z-10"
                                aria-label={`Repeated ${getMealCount(meal.name)} times`}
                                title={`This meal appears ${getMealCount(meal.name)} times this week`}
                              >
                                <span className="text-[#643C35] text-xs font-semibold">
                                  {getMealCount(meal.name)}x
                                </span>
                              </div>
                            )}
                            
                            <button
                              onClick={() => onViewMealDetails(meal.id)}
                              className="w-full text-left"
                            >
                              <div className="flex items-center gap-4 mb-3">
                                <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-100">
                                  <ImageWithFallback
                                    src={meal.image}
                                    alt={meal.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="mb-2 line-clamp-2">{meal.name}</h3>
                                  <div className="flex flex-wrap gap-2 mb-1">
                                    {meal.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className="px-2 py-1 bg-[#E8F0ED] text-[#6B8A78] rounded-full text-xs"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                  <p className="text-sm text-[#757575]">Serves {meal.serves}</p>
                                </div>
                              </div>
                            </button>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSwap(meal.id);
                                }}
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
                              >
                                <Shuffle className="w-4 h-4 text-gray-600" />
                                <span className="text-gray-600">Swap</span>
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmationMealId(meal.id);
                                }}
                                className="p-1.5 rounded-lg bg-[#F3F4F6] hover:bg-[#B85A5A] transition-colors group"
                                aria-label={`Remove ${meal.name}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-[#525252] group-hover:text-white transition-colors" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white rounded-3xl p-6 border-2 border-dashed border-gray-200 flex items-center justify-center">
                            <span className="text-sm text-[#757575]">No {mealType.toLowerCase()} planned</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-3xl p-8 text-center border-2 border-dashed border-gray-200">
                  <p className="text-[#757575]">No meals planned for this day</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Legend */}
        <div className="mt-6 bg-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center shadow-sm">
              <span className="text-[#643C35] text-xs font-semibold">2x</span>
            </div>
            <span className="text-sm text-[#757575]">= Meal appears multiple times this week</span>
          </div>
        </div>
      </div>

      {/* Confirmation Drawer */}
      <ConfirmationDrawer
        isOpen={confirmationMealId !== null}
        onCancel={() => setConfirmationMealId(null)}
        onConfirm={() => {
          if (confirmationMealId) {
            onRemove(confirmationMealId);
            setConfirmationMealId(null);
          }
        }}
        title="Remove Meal"
        message="Are you sure you want to remove this meal from the plan?"
        confirmLabel="Remove"
      />
    </div>
    
  );
}
